import type {
  CustomInstanceActualState,
  CustomInstanceListParams,
} from '../types/custom';

export const CUSTOM_INSTANCE_STATES: CustomInstanceActualState[] = [
  'preparing',
  'prepared',
  'creating',
  'starting',
  'running',
  'stopping',
  'stopped',
  'missing',
  'deleting',
  'error',
  'deleted',
];

export const CUSTOM_INSTANCE_SORTS: Array<NonNullable<CustomInstanceListParams['sort']>> = [
  'createdAt',
  'updatedAt',
  'name',
  'state',
];

const positiveInteger = (value: string | null, fallback: number, max: number): number => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? Math.min(parsed, max) : fallback;
};

export function readCustomInstanceListParams(
  searchParams: URLSearchParams,
  fixedProductId?: string,
): Required<Pick<CustomInstanceListParams, 'sort' | 'dir' | 'page' | 'pageSize' | 'includeDeleted'>>
  & Omit<CustomInstanceListParams, 'sort' | 'dir' | 'page' | 'pageSize' | 'includeDeleted'> {
  const q = searchParams.get('q')?.trim();
  const productId = fixedProductId || searchParams.get('productId')?.trim();
  const requestedState = searchParams.get('state');
  const requestedSort = searchParams.get('sort');
  const requestedDir = searchParams.get('dir');
  const requestedPageSize = positiveInteger(searchParams.get('pageSize'), 25, 100);
  return {
    ...(q ? { q } : {}),
    ...(productId ? { productId } : {}),
    ...(CUSTOM_INSTANCE_STATES.includes(requestedState as CustomInstanceActualState)
      ? { state: requestedState as CustomInstanceActualState }
      : {}),
    sort: CUSTOM_INSTANCE_SORTS.includes(requestedSort as NonNullable<CustomInstanceListParams['sort']>)
      ? requestedSort as NonNullable<CustomInstanceListParams['sort']>
      : 'createdAt',
    dir: requestedDir === 'asc' ? 'asc' : 'desc',
    page: positiveInteger(searchParams.get('page'), 1, 1_000_000),
    pageSize: [10, 25, 50, 100].includes(requestedPageSize) ? requestedPageSize : 25,
    includeDeleted: ['1', 'true'].includes(searchParams.get('includeDeleted') || ''),
  };
}

export function customInstanceListSearch(params: CustomInstanceListParams): string {
  const result = new URLSearchParams();
  if (params.q) result.set('q', params.q);
  if (params.productId) result.set('productId', params.productId);
  if (params.state) result.set('state', params.state);
  if (params.sort && params.sort !== 'createdAt') result.set('sort', params.sort);
  if (params.dir && params.dir !== 'desc') result.set('dir', params.dir);
  if (params.page && params.page !== 1) result.set('page', String(params.page));
  if (params.pageSize && params.pageSize !== 25) result.set('pageSize', String(params.pageSize));
  if (params.includeDeleted) result.set('includeDeleted', '1');
  const serialized = result.toString();
  return serialized ? `?${serialized}` : '';
}
